// #region: Class definition *********************************************************************/
/**
 * @class FileUpload
 * @description Custom web component that renders a file upload button with optional preview container integration.
 * Supports multiple file selection, upload, and automatic preview management through cross-component communication.
 * 
 * @extends WebComponent
 * 
 * @example
 * // Basic usage
 * <file-upload accept=".pdf,.jpg,.png" 
 *              multiple
 *              api-path="/api/upload/attachments"
 *              default-label="Select Files"
 *              selected-label="Files Selected ✔"
 *              success-callback="onUploadSuccess"
 *              error-callback="onUploadError">
 * </file-upload>
 * 
 * @example
 * // With external preview container and file counter
 * <file-upload id="upload-button"
 *              accept=".pdf,.jpg,.png"
 *              multiple
 *              api-path="/api/upload/attachments"
 *              preview-container-id="attachment-preview"
 *              show-counter
 *              default-label="Select Files"
 *              success-callback="onUploadSuccess">
 * </file-upload>
 * <preview-container id="attachment-preview"></preview-container>
 * 
 * @property {string} accept - The accepted file types (e.g., ".pdf,.jpg,.png").
 * @property {string} api-path - The API path for file upload.
 * @property {string} camera-facing - Camera facing mode: "user" (front) or "environment" (back). Defaults to "environment".
 * @property {string} camera-icon - CSS class for the camera button icon (e.g., "lf-icon-photo-camera").
 * @property {string} default-label - The label for the button when no file is selected.
 * @property {string} files-icon - CSS class for the files button icon (e.g., "lf-icon-folder").
 * @property {string} error-callback - Name of the global function to call on upload error.
 * @property {boolean} file-present - Whether a file is currently present in the system.
 * @property {string} icon - CSS class for the button icon (e.g., "lf-icon-attach-file").
 * @property {boolean} multiple - Whether multiple file selection is allowed.
 * @property {string} preview-container-id - ID of an external preview-container to connect to.
 * @property {string} selected-label - The label for the button when file(s) is/are selected.
 * @property {boolean} show-counter - Whether to show file count in button label (e.g., "Select (3)").
 * @property {string} success-callback - Name of the global function to call on successful upload.
 * 
 * @remarks
 * Cross-Component Communication:
 * - When `preview-container-id` is set, the component automatically connects to the specified preview-container.
 * - Files are automatically added to the preview container after successful upload.
 * - File count updates are handled automatically through DOM events.
 * - The preview container can derive its delete API path from this component's `api-path`.
 * 
 * Camera Functionality:
 * - Users can take multiple photos, review them, delete unwanted ones, and upload all at once.
 * - The `camera-facing` attribute controls which camera to use: "environment" (back) or "user" (front).
 * - Camera interface opens in a headless dialog-window for consistent experience across all devices.
 * - Camera functionality gracefully falls back to regular file selection on devices without camera support.
 * 
 * Events Dispatched:
 * - `file-uploaded`: Dispatched after successful file upload with file details.
 * 
 * Events Listened:
 * - `attachment-added`: From connected preview-container to update file presence.
 * - `attachment-removed`: From connected preview-container to update file presence.
 */
class FileUpload extends WebComponent {

    // #region: Static Data **********************************************************************/
    /**
     * High resolution capture width for photo quality.
     * @type {number}
     * @static
     * @memberof FileUpload
     */
    static CAPTURE_WIDTH = 1920;

    /**
     * High resolution capture height for photo quality.
     * @type {number}
     * @static
     * @memberof FileUpload
     */
    static CAPTURE_HEIGHT = 1920;

    /**
    * Maximum long-edge resolution (in pixels) for files uploaded from the device-camera path.
    * Mobile photos (iPad ~12MP) are downscaled to this cap before upload to keep payloads and server-side processing reasonable.
    * Chosen to preserve visible detail for inspection photography.
    * @type {number}
    * @static
    * @memberof FileUpload
    */
    static MAX_UPLOAD_EDGE = 2048;

    /**
     * JPEG quality (0–1) used when re-encoding a downscaled upload.
     * @type {number}
     * @static
     * @memberof FileUpload
     */
    static UPLOAD_JPEG_QUALITY = 0.9;

    /**
     * Default video height for camera constraints (portrait orientation).
     * @type {number}
     * @static
     * @memberof FileUpload
     */
    static VIDEO_HEIGHT = 1920;

    /**
     * Default video width for camera constraints (portrait orientation).
     * @type {number}
     * @static
     * @memberof FileUpload
     */
    static VIDEO_WIDTH = 1080;

    /**
     * Zoom step increment for zoom in/out operations.
     * @type {number}
     * @static
     * @memberof FileUpload
     */
    static ZOOM_STEP = 0.2;
    // #endregion ********************************************************************************/

    // #region: Private Fields *******************************************************************/
    /**
     * Button text span element.
     * @type {HTMLSpanElement}
     * @private
     * @memberof FileUpload
     */
    #buttonText;

    /**
     * Whether the component has been disconnected from the DOM.
     * Used as defense-in-depth to prevent leaked handlers from executing.
     * @type {boolean}
     * @private
     * @memberof FileUpload
     */
    #isDestroyed = false;

    /**
     * Camera dialog window instance.
     * @type {Object|null}
     * @private
     * @memberof FileUpload
     */
    #cameraDialog;

    /**
     * Active camera media stream.
     * @type {MediaStream|null}
     * @private
     * @memberof FileUpload
     */
    #cameraStream;

    /**
     * Array of captured photo files.
     * @type {File[]}
     * @private
     * @memberof FileUpload
     */
    #capturedPhotos;

    /**
     * Array of metadata for captured photos.
     * @type {Object[]}
     * @private
     * @memberof FileUpload
     */
    #capturedPhotosMetadata;

    /**
     * Choice dialog overlay element.
     * @type {HTMLDivElement|null}
     * @private
     * @memberof FileUpload
     */
    #choiceDialog;

    /**
     * Hidden file input element.
     * @type {HTMLInputElement}
     * @private
     * @memberof FileUpload
     */
    #fileInput;

    /**
     * Hidden input element for the OS-native camera (uses `capture` attribute).
     * Separate from #fileInput because `capture` forces single-shot and disables
     * gallery picking — we only want that on the device-camera path.
     * @type {HTMLInputElement|null}
     * @private
     * @memberof FileUpload
     */
    #deviceCameraInput;

    /**
     * Multi-shot overlay shown between device-camera captures.
     * @type {HTMLDivElement|null}
     * @private
     * @memberof FileUpload
     */
    #deviceCameraOverlay;

    /**
     * Full-screen busy overlay shown during an in-flight upload. Tracked on the
     * instance so disconnectedCallback can clear it if the component is torn down
     * mid-upload.
     * @type {HTMLDivElement|null}
     * @private
     * @memberof FileUpload
     */
    #busyOverlay;

    /**
     * Initial pinch distance for zoom gestures.
     * @type {number|null}
     * @private
     * @memberof FileUpload
     */
    #initialPinchDistance;

    /**
     * Initial zoom level for pinch gestures.
     * @type {number|null}
     * @private
     * @memberof FileUpload
     */
    #initialZoomLevel;

    /**
     * Maximum zoom level for camera.
     * @type {number}
     * @private
     * @memberof FileUpload
     */
    #maxZoom;

    /**
     * Minimum zoom level for camera.
     * @type {number}
     * @private
     * @memberof FileUpload
     */
    #minZoom;

    /**
     * Connected preview container element.
     * @type {Object|null}
     * @private
     * @memberof FileUpload
     */
    #previewContainer;

    /**
     * Main upload button element.
     * @type {HTMLButtonElement}
     * @private
     * @memberof FileUpload
     */
    #uploadButton;

    /**
     * Video element for camera stream.
     * @type {HTMLVideoElement|null}
     * @private
     * @memberof FileUpload
     */
    #videoElement;

    /**
     * Current zoom level for camera.
     * @type {number}
     * @private
     * @memberof FileUpload
     */
    #zoomLevel;

    /**
     * Cached locale resources for this component.
     * @type {Object}
     *
     * @private
     * @memberof FileUpload
     */
    #locales;
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * Creates an instance of FileUpload.
     * 
     * @memberof FileUpload
     */
    constructor() {
        super();

        // Initialize captured photos array.
        this.#capturedPhotos = [];
        this.#capturedPhotosMetadata = [];

        // Initialize zoom properties.
        this.#zoomLevel = 1;
        this.#maxZoom = 8;
        this.#minZoom = 1;

        // We validate, but not on change.
        this.setAttribute('data-novalidate-onchange', '');

        // Register locale namespaces used by this component and cache resources.
        this.#locales = this.registerLocaleNamespace();
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Registered tag name for this web component.
     * This is used by the browser to identify and register the custom element.
     * @type {string}
     * @static
     * @memberof FileUpload
     */
    static tagName = 'file-upload';

    /**
     * Identifies class instance.
     * @returns {string} The string representation of this class instance.
     * 
     * @memberof FileUpload
     */
    toString() {
        return '[FileUpload]';
    }

    /**
     * Identifies class.
     * @returns {string} The string representation of this class.
     * 
     * @static
     * @memberof FileUpload
     */
    static toString() {
        return '[FileUpload]';
    }

    /**
     * Identifies the version.
     * @returns {string} The version number of this class.
     * 
     * @static
     * @memberof FileUpload
     */
    static version() {
        return '2.1.3';
    }
    // #endregion ********************************************************************************/

    // #region: Static Properties ****************************************************************/
    /**
     * Defines the observed attributes for the custom element.
     * @returns {string[]} Array of attribute names to observe.
     * 
     * @static
     * @memberof FileUpload
     */
    static get observedAttributes() {
        return [...(super.observedAttributes || []), 'accept', 'camera-facing', 'camera-icon', 'default-label', 'device-camera-icon', 'error-callback', 'file-present', 'files-icon', 'icon', 'preview-container-id', 'selected-label', 'show-counter', 'success-callback'];
    }
    // #endregion ********************************************************************************/

    // #region: Properties ***********************************************************************/
    /**
     * Accept attribute for file types.
     * @returns {string} The accepted file types.
     * 
     * @memberof FileUpload
     */
    get accept() {
        return this.getAttribute('accept');
    }

    /**
     * Sets the accept attribute for file types.
     * @param {string} value The accepted file types.
     * 
     * @memberof FileUpload
     */
    set accept(value) {
        this.setAttribute('accept', value);
    }

    /**
     * API path for the file upload.
     * @returns {string} The API path.
     * 
     * @memberof FileUpload
     */
    get apiPath() {
        return this.getAttribute('api-path');
    }

    /**
     * Sets the API path for the file upload.
     * @param {string} value The API path.
     * 
     * @memberof FileUpload
     */
    set apiPath(value) {
        this.setAttribute('api-path', value);
    }

    /**
     * Camera facing mode for devices with camera support.
     * @returns {string} The camera facing mode: "user" or "environment". Defaults to "environment".
     * 
     * @memberof FileUpload
     */
    get cameraFacing() {
        return this.getAttribute('camera-facing') || 'environment';
    }

    /**
     * Sets the camera facing mode for devices with camera support.
     * @param {string} value The camera facing mode: "user" or "environment".
     * 
     * @memberof FileUpload
     */
    set cameraFacing(value) {
        this.setAttribute('camera-facing', value);
    }

    /**
     * Gets the icon class name on the camera button.
     * @returns {string|null} The icon class name or null if not set.
     * 
     * @memberof FileUpload
     */
    get cameraIcon() {
        return this.getAttribute('camera-icon');
    }

    /**
     * Sets the icon class name on the camera button.
     * @param {string} value The icon class name to set.
     * 
     * @memberof FileUpload
     */
    set cameraIcon(value) {
        this.setAttribute('camera-icon', value);
    }

    /**
     * Gets the icon class name on the device-camera button.
     * @returns {string|null} The icon class name or null if not set.
     *
     * @memberof FileUpload
     */
    get deviceCameraIcon() {
        return this.getAttribute('device-camera-icon');
    }

    /**
     * Sets the icon class name on the device-camera button.
     * @param {string} value The icon class name to set.
     *
     * @memberof FileUpload
     */
    set deviceCameraIcon(value) {
        this.setAttribute('device-camera-icon', value);
    }

    /**
     * Label for the button when no file is selected.
     * @returns {string} The default label text.
     * 
     * @memberof FileUpload
     */
    get defaultLabel() {
        if (this.getAttribute('default-label')) {
            return this.getAttribute('default-label');
        }

        return this.#locales?.uploadFile || 'Bestand uploaden';
    }

    /**
     * Sets the label for the button when no file is selected.
     * @param {string} value The default label text.
     * 
     * @memberof FileUpload
     */
    set defaultLabel(value) {
        this.setAttribute('default-label', value);
    }

    /**
     * Name of the global function to call when upload encounters an error.
     * @returns {string|null} The error callback function name.
     * 
     * @memberof FileUpload
     */
    get errorCallback() {
        return this.getAttribute('error-callback');
    }

    /**
     * Sets the name of the global function to call when upload encounters an error.
     * @param {string} value The error callback function name.
     * 
     * @memberof FileUpload
     */
    set errorCallback(value) {
        this.setAttribute('error-callback', value);
    }

    /**
     * Is a file that is to be uploaded present within the system (previous uploaded).
     * @returns {boolean} True when a file is present.
     * 
     * @memberof FileUpload
     */
    get filePresent() {
        return this.hasAttribute('file-present');
    }

    /**
     * Sets whether a file is present within the system.
     * @param {boolean} value True when a file is present.
     * 
     * @memberof FileUpload
     */
    set filePresent(value) {
        if (value) {
            this.setAttribute('file-present', '');
        }
        else {
            this.removeAttribute('file-present');
        }
    }

    /**
     * Gets the icon class name on the files button.
     * @returns {string|null} The icon class name or null if not set.
     * 
     * @memberof FileUpload
     */
    get filesIcon() {
        return this.getAttribute('files-icon');
    }

    /**
     * Sets the icon class name on the files button.
     * @param {string} value The icon class name to set.
     * 
     * @memberof FileUpload
     */
    set filesIcon(value) {
        this.setAttribute('files-icon', value);
    }

    /**
     * Gets the icon class name on the button.
     * @returns {string|null} The icon class name or null if not set.
     * 
     * @memberof FileUpload
     */
    get icon() {
        return this.getAttribute('icon');
    }

    /**
     * Sets the icon class name on the button.
     * @param {string} value The icon class name to set.
     * 
     * @memberof FileUpload
     */
    set icon(value) {
        this.setAttribute('icon', value);
    }

    /**
    * Attribute for allowing multiple file selection.
    * @returns {boolean} True if multiple files are allowed, false otherwise.
    * 
    * @memberof FileUpload
    */
    get multiple() {
        return this.hasAttribute('multiple');
    }

    /**
     * Label for the button when a file is selected.
     * @returns {string} The selected label text.
     * 
     * @memberof FileUpload
     */
    get selectedLabel() {
        if (this.getAttribute('selected-label')) {
            return this.getAttribute('selected-label');
        }

        return this.defaultLabel + '\u00A0\u00A0✔';
    }

    /**
     * Sets the label for the button when a file is selected.
     * @param {string} value The selected label text.
     * 
     * @memberof FileUpload
     */
    set selectedLabel(value) {
        this.setAttribute('selected-label', value);
    }

    /**
     * Whether to show file counter in the button label.
     * @returns {boolean} True if file counter should be shown.
     * 
     * @memberof FileUpload
     */
    get showCounter() {
        return this.hasAttribute('show-counter');
    }

    /**
     * Sets whether to show file counter in the button label.
     * @param {boolean} value True to show file counter.
     * 
     * @memberof FileUpload
     */
    set showCounter(value) {
        if (value) {
            this.setAttribute('show-counter', '');
        }
        else {
            this.removeAttribute('show-counter');
        }
    }

    /**
     * ID of the external preview container to connect to.
     * @returns {string|null} The preview container ID.
     * 
     * @memberof FileUpload
     */
    get previewContainerId() {
        return this.getAttribute('preview-container-id');
    }

    /**
     * Sets the ID of the external preview container to connect to.
     * @param {string} value The preview container ID.
     * 
     * @memberof FileUpload
     */
    set previewContainerId(value) {
        this.setAttribute('preview-container-id', value);
    }

    /**
     * Name of the global function to call when upload succeeds.
     * @returns {string|null} The success callback function name.
     * 
     * @memberof FileUpload
     */
    get successCallback() {
        return this.getAttribute('success-callback');
    }

    /**
     * Sets the name of the global function to call when upload succeeds.
     * @param {string} value The success callback function name.
     * 
     * @memberof FileUpload
     */
    set successCallback(value) {
        this.setAttribute('success-callback', value);
    }
    // #endregion ********************************************************************************/

    // #region: Lifecycle events *****************************************************************/
    /**
     * Called when observed attributes change.
     * @param {string} name The name of the attribute that changed.
     * @param {string|null} oldValue The previous value of the attribute.
     * @param {string|null} newValue The new value of the attribute.
     * 
     * @memberof FileUpload
     */
    attributeChangedCallback(name, oldValue, newValue) {
        // Call parent's implementation.
        super.attributeChangedCallback(name, oldValue, newValue);

        if (oldValue === newValue) {
            return;
        }

        switch (name) {

            case 'accept': {
                if (!this.#fileInput) {
                    break;
                }

                if (newValue) {
                    this.#fileInput.accept = newValue;
                }
                else {
                    this.#fileInput.removeAttribute('accept');
                }

                break;
            }

            case 'default-label':
            case 'file-present':
            case 'selected-label':
            case 'show-counter':
                this.#updateLabel();

                break;

            case 'preview-container-id':
                this.#connectToPreviewContainer();

                break;

            case 'read-only':
                // Base class handles the attribute, we just apply the state.
                this.#applyReadOnlyState();

                break;

            default:
                // Nothing!
                break;

        }
    }
    // #endregion ********************************************************************************/

    // #region: Events ***************************************************************************/
    /**
     * Handles dialog close from any source (native button, ESC, etc.).
     * 
     * @private
     * @memberof FileUpload
     */
    #handleDialogClose() {
        // Stop camera stream if still active.
        if (this.#cameraStream) {
            this.#cameraStream.getTracks().forEach((track) => {
                track.stop();
            });

            this.#cameraStream = null;
        }

        // Clean up dialog resources.
        if (this.#cameraDialog) {
            // Clean up orientation event listeners.
            if (this.#cameraDialog.orientationHandlers) {
                this.#cameraDialog.orientationHandlers.forEach(({ event, handler }) => {
                    window.removeEventListener(event, handler);
                });
            }

            // Clean up visibility event listeners.
            if (this.#cameraDialog.visibilityHandlers) {
                this.#cameraDialog.visibilityHandlers.forEach(({ event, handler, target }) => {
                    target.removeEventListener(event, handler);
                });
            }

            // Reset state.
            this.#zoomLevel = 1;
            this.#capturedPhotos = [];
            this.#capturedPhotosMetadata = [];
            this.#videoElement = null;

            // Destroy dialog if not already destroyed.
            this.#cameraDialog?.destroy();
            this.#cameraDialog = null;
        }
    }

    /**
     * Handles error response with detailed error processing.
     * @param {Response} response The fetch response object.
     * 
     * @private
     * @memberof FileUpload
     */
    async #handleErrorResponse(response) {
        try {
            const responseText = await response.text();

            try {
                // Try to parse the JSON response for detailed error information.
                const responseJson = JSON.parse(responseText);

                // Handle individual file errors.
                if (responseJson.errors && Array.isArray(responseJson.errors)) {
                    responseJson.errors.forEach((error) => {
                        core.notify('error', `${error.fileName}: ${error.error}`, { duration: 0 });
                    });

                    return;
                }
            }
            catch (parseError) {
                if (responseText === 'No file provided' || responseText === '"No file provided"') {
                    core.notify('error', this.#locales?.noFile || 'Geen bestand opgegeven');

                    return;
                }
            }

            // Default error message.
            core.notify('error', this.#locales?.uploadError || 'Upload mislukt');
        }
        catch (error) {
            // Fallback to basic error handling.
            leanformsNext.onAjaxError(response);
        }
    }

    /**
     * Handles end of pinch gesture.
     * 
     * @private
     * @memberof FileUpload
     */
    #handlePinchEnd() {
        this.#initialPinchDistance = null;
        this.#initialZoomLevel = null;
    }

    /**
     * Handles pinch gesture for zoom on touch devices.
     * 
     * @private
     * @memberof FileUpload
     * @param {TouchEvent} event The touch event.
     */
    #handlePinchGesture(event) {
        if (event.touches.length === 2 && this.#initialPinchDistance !== null) {
            const touch1 = event.touches[0];
            const touch2 = event.touches[1];
            const distance = Math.sqrt(((touch2.clientX - touch1.clientX) ** 2) + ((touch2.clientY - touch1.clientY) ** 2));

            const scale = distance / this.#initialPinchDistance;
            const newZoom = this.#initialZoomLevel * scale;
            this.#setZoomLevel(newZoom);
        }
    }

    /**
     * Handles start of pinch gesture.
     * 
     * @private
     * @memberof FileUpload
     * @param {TouchEvent} event The touch event.
     */
    #handlePinchStart(event) {
        if (event.touches.length === 2) {
            const touch1 = event.touches[0];
            const touch2 = event.touches[1];
            const distance = Math.sqrt(((touch2.clientX - touch1.clientX) ** 2) + ((touch2.clientY - touch1.clientY) ** 2));

            this.#initialPinchDistance = distance;
            this.#initialZoomLevel = this.#zoomLevel;
        }
    }

    /**
     * Handles explicit user gesture to open camera interface.
     *
     * @private
     * @memberof FileUpload
     */
    async #onOpenCamera() {
        const cameraStatus = await window.permissionsManager.getCameraPermissionStatus();

        if (cameraStatus === 'denied') {
            return;
        }

        if (cameraStatus === 'prompt') {
            const granted = await window.permissionsManager.requestCameraPermission({ video: true, audio: false });

            if (granted) {
                return this.#openCameraInterface();
            }
            else {
                return;
            }
        }

        return this.#openCameraInterface();
    }

    /**
     * Handles explicit user gesture to open the OS-native camera via `<input capture>`.
     * Reuses #capturedPhotos so the device-camera path feeds the same upload pipeline
     * as the in-page viewfinder path.
     *
     * @private
     * @memberof FileUpload
     */
    #onOpenDeviceCamera() {
        this.#ensureDeviceCameraInput();
        this.#deviceCameraInput.click();
    }
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * Adds an attachment to the integrated preview container.
     * @param {Object} file File object to add.
     * 
     * @memberof FileUpload
     */
    addAttachment(file) {
        if (this.#previewContainer) {
            this.#previewContainer.addAttachment(file);
        }
    }

    /**
     * Clears all attachments from the integrated preview container.
     * 
     * @memberof FileUpload
     */
    clearAttachments() {
        if (this.#previewContainer) {
            this.#previewContainer.clearAttachments();
        }
    }

    /**
     * Gets attachment data from the integrated preview container.
     * @returns {Array} Array of attachment data objects.
     * 
     * @memberof FileUpload
     */
    getAttachmentData() {
        if (this.#previewContainer) {
            return this.#previewContainer.getAttachmentData();
        }

        return [];
    }

    /**
     * Initializes this web component.
     * Runs on document load (see: connectedCallback).
     */
    initialize() {
        // Pass on the class property to the actual button rendered (and remove from the component).
        this.#uploadButton = document.createElement('button');
        this.#uploadButton.type = 'button';
        this.#uploadButton.className = this.className;
        this.className = '';

        // Create input.
        this.#fileInput = document.createElement('input');
        this.#fileInput.type = 'file';

        if (this.accept) {
            this.#fileInput.accept = this.accept;
        }

        this.#fileInput.setAttribute('hidden', '');

        if (this.multiple) {
            this.#fileInput.multiple = true;
        }

        // Set label and icon.
        this.#buttonText = document.createElement('span');

        if (this.icon) {
            const buttonIcon = document.createElement('span');

            buttonIcon.classList.add('button-icon', this.icon);
            this.#uploadButton.appendChild(buttonIcon);
        }

        this.#uploadButton.appendChild(this.#buttonText);

        // Add to the DOM.
        this.appendChild(this.#fileInput);
        this.appendChild(this.#uploadButton);

        // Connect to external preview container if specified.
        this.#connectToPreviewContainer();

        // Bind event listeners.
        this.#fileInput.addEventListener('change', () => {
            return this.#uploadFile();
        });

        this.#uploadButton.addEventListener('click', () => {
            // Check camera availability and show choice dialog or file input.
            if (coreUI.isMobile()) {
                // Show choice dialog or camera immediately on mobile.
                this.#createChoiceDialog();
            }
            else {
                // Desktop: use file input directly.
                this.#fileInput.click();
            }
        });

        // Set initial label states.
        this.#updateLabel();
    }

    /**
     * Sets attachments in the integrated preview container.
     * @param {Array} attachments Array of attachment objects.
     * 
     * @memberof FileUpload
     */
    setAttachments(attachments) {
        if (this.#previewContainer) {
            this.#previewContainer.setAttachments(attachments);

            // Update file presence state after setting attachments.
            this.#updateFilePresence();
        }
    }

    /**
     * Lifecycle callback when element is removed from the DOM.
     * Releases camera resources and marks the component as destroyed to prevent leaked handlers from executing.
     *
     * @memberof FileUpload
     */
    disconnectedCallback() {
        super.disconnectedCallback();

        this.#isDestroyed = true;

        // Ensure camera resources are released.
        if (this.#cameraStream) {
            this.#cameraStream.getTracks().forEach((track) => {
                track.stop();
            });

            this.#cameraStream = null;
        }

        this.#closeDeviceCameraOverlay();
        this.#hideUploadBusy();

        if (this.#deviceCameraInput) {
            this.#deviceCameraInput.remove();
            this.#deviceCameraInput = null;
        }
    }

    /**
     * Validate the content.
     * @returns {boolean} True if valid, false if not. Defaults to true.
     *
     * @memberof FileUpload
     */
    validate() {
        if (this.hasAttribute('required') && !this.filePresent) {
            validation.setErrorMessage(this, resources.validationMessages.valuemissing);

            return false;
        }

        return true;
    }
    // #endregion ********************************************************************************/

    // #region: Private Methods ******************************************************************/
    /**
     * Appends a thumbnail for a newly captured photo. Uses createImageBitmap with
     * resize options so a multi-megapixel file (iPad photos are ~12MP) is decoded
     * straight to a tiny bitmap instead of fully decoded and scaled — an order of
     * magnitude less work and memory per thumb.
     *
     * @private
     * @memberof FileUpload
     */
    async #addDeviceCameraThumb(file) {
        const thumbs = this.#deviceCameraOverlay?.querySelector('.device-camera-thumbs');

        if (!thumbs) {
            return;
        }

        // Reserve the slot synchronously so thumbs stay in capture order even if a later
        // decode finishes sooner than an earlier one.
        const canvas = document.createElement('canvas');

        canvas.className = 'device-camera-thumb';
        thumbs.appendChild(canvas);

        const THUMB_PX = 96;

        try {
            const bitmap = await createImageBitmap(file, {
                resizeWidth: THUMB_PX,
                resizeQuality: 'low'
            });

            if (!canvas.isConnected) {
                bitmap.close();

                return;
            }

            canvas.width = bitmap.width;
            canvas.height = bitmap.height;
            canvas.getContext('2d').drawImage(bitmap, 0, 0);
            bitmap.close();
        }
        catch {
            // Fallback for browsers without resize support: plain object URL.
            const img = document.createElement('img');

            img.className = 'device-camera-thumb';
            img.src = URL.createObjectURL(file);
            img.alt = '';
            canvas.replaceWith(img);
        }
    }

    /**
     * Returns a re-encoded, downscaled JPEG suitable for upload if the source is larger
     * than MAX_UPLOAD_EDGE on its long side, otherwise returns the original file.
     *
     * @private
     * @memberof FileUpload
     * @param {File} file The original file from the device camera.
     * @returns {Promise<File>} The downscaled file, or the original if no work was needed.
     */
    async #downscaleForUpload(file) {
        if (!file.type.startsWith('image/')) {
            return file;
        }

        const bitmap = await createImageBitmap(file);
        const longEdge = Math.max(bitmap.width, bitmap.height);

        if (longEdge <= FileUpload.MAX_UPLOAD_EDGE) {
            bitmap.close();

            return file;
        }

        const scale = FileUpload.MAX_UPLOAD_EDGE / longEdge;
        const targetWidth = Math.round(bitmap.width * scale);
        const targetHeight = Math.round(bitmap.height * scale);
        const canvas = document.createElement('canvas');

        canvas.width = targetWidth;
        canvas.height = targetHeight;
        canvas.getContext('2d').drawImage(bitmap, 0, 0, targetWidth, targetHeight);
        bitmap.close();

        const blob = await new Promise((resolve) => {
            canvas.toBlob(resolve, 'image/jpeg', FileUpload.UPLOAD_JPEG_QUALITY);
        });

        // Release canvas memory (iOS Safari is strict about retained bitmaps).
        canvas.width = 0;
        canvas.height = 0;

        if (!blob) {
            return file;
        }

        const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';

        return new File([blob], name, { type: 'image/jpeg', lastModified: file.lastModified });
    }

    /**
     * Applies read-only state to all relevant elements.
     * 
     * @private
     * @memberof MarkerSelector
     */
    #applyReadOnlyState() {
        // Disable/enable button.
        if (this.readOnly) {
            this.#uploadButton.setAttribute('disabled', '');
        }
        else {
            this.#uploadButton.removeAttribute('disabled');
        }
    }

    /**
     * Applies zoom transformation to the video element.
     * 
     * @private
     * @memberof FileUpload
     */
    #applyZoom() {
        if (this.#videoElement) {
            this.#videoElement.style.transform = `scale(${this.#zoomLevel})`;
        }
    }

    /**
     * Cancels photo capture and closes the camera.
     * 
     * @private
     * @memberof FileUpload
     */
    async #cancelCapture() {
        this.#capturedPhotos = [];
        this.#capturedPhotosMetadata = [];

        await this.#closeCameraDialog();
    }

    /**
     * Captures a photo from the camera stream matching the visible video area.
     *
     * @private
     * @memberof FileUpload
     */
    async #capturePhoto() {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        // Get video element dimensions (what user sees).
        const videoDisplayWidth = this.#videoElement.clientWidth;
        const videoDisplayHeight = this.#videoElement.clientHeight;

        // Get actual video stream dimensions.
        const videoStreamWidth = this.#videoElement.videoWidth;
        const videoStreamHeight = this.#videoElement.videoHeight;

        // Match the viewfinder's actual object-fit so WYSIWYG holds in both orientations:
        // portrait uses `cover` (crop to fill) while landscape uses `contain` (letterbox).
        const { objectFit } = getComputedStyle(this.#videoElement);
        const displayAspectRatio = videoDisplayWidth / videoDisplayHeight;
        const streamAspectRatio = videoStreamWidth / videoStreamHeight;

        let sourceHeight = videoStreamHeight,
            sourceWidth = videoStreamWidth,
            sourceX = 0,
            sourceY = 0;

        if (objectFit === 'cover') {
            if (streamAspectRatio > displayAspectRatio) {
                sourceWidth = videoStreamHeight * displayAspectRatio;
                sourceX = (videoStreamWidth - sourceWidth) / 2;
            }
            else {
                sourceHeight = videoStreamWidth / displayAspectRatio;
                sourceY = (videoStreamHeight - sourceHeight) / 2;
            }
        }

        // Apply zoom factor to crop the source area.
        const zoomedWidth = sourceWidth / this.#zoomLevel;
        const zoomedHeight = sourceHeight / this.#zoomLevel;
        const zoomedX = sourceX + ((sourceWidth - zoomedWidth) / 2);
        const zoomedY = sourceY + ((sourceHeight - zoomedHeight) / 2);

        // Canvas aspect follows the cropped source, not the display — otherwise `contain`
        // mode would bake the letterbox into the saved image.
        const captureAspectRatio = sourceWidth / sourceHeight;
        let canvasHeight, canvasWidth;

        if (captureAspectRatio >= 1) {
            canvasWidth = FileUpload.CAPTURE_WIDTH;
            canvasHeight = canvasWidth / captureAspectRatio;
        }
        else {
            canvasHeight = FileUpload.CAPTURE_HEIGHT;
            canvasWidth = canvasHeight * captureAspectRatio;
        }

        canvas.width = canvasWidth;
        canvas.height = canvasHeight;

        // Draw only the visible zoomed portion of the video.
        context.drawImage(this.#videoElement,
                          zoomedX, zoomedY, zoomedWidth, zoomedHeight,
                          0, 0, canvas.width, canvas.height);

        const JPEG_QUALITY = 0.95;

        canvas.toBlob(async (blob) => {
            // Release canvas GPU memory immediately (critical for iOS Safari memory limits).
            context.clearRect(0, 0, canvas.width, canvas.height);
            canvas.width = 0;
            canvas.height = 0;

            const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
            let gpsPosition = null;

            try {
                gpsPosition = await window.gps.getCachedPosition();
            }
            catch (error) {
                if (error.type === 'POSITION_UNAVAILABLE') {
                    if (this.isAndroid) {
                        core.notify('warning', this.#locales?.gpsDisabledWarningAndroid || 'Locatiediensten zijn uitgeschakeld. Uw uploads bevatten geen GPS-coördinaten. Om in te schakelen: Ga naar Instellingen > Locatie > schakel "Locatie gebruiken" in.', { duration: 10000 });
                    }
                    else if (this.isIos) {
                        core.notify('warning', this.#locales?.gpsDisabledWarningIos || 'Locatiediensten zijn uitgeschakeld. Uw uploads bevatten geen GPS-coördinaten. Om in te schakelen: Ga naar Instellingen > Privacy en Beveiliging > Locatievoorzieningen > schakel "Locatievoorzieningen" in.', { duration: 10000 });
                    }
                    else {
                        core.notify('warning', this.#locales?.gpsDisabledWarning || 'Locatiediensten zijn uitgeschakeld. Uw uploads bevatten geen GPS-coördinaten.', { duration: 10000 });
                    }
                }
            }

            this.#capturedPhotos.push(file);
            this.#capturedPhotosMetadata.push({
                gps: gpsPosition,
                fromCamera: true
            });

            this.#updateCaptureBadge();
            this.#updateDoneButton();
        }, 'image/jpeg', JPEG_QUALITY);
    }

    /**
     * Closes the camera dialog and stops the camera stream.
     * 
     * @private
     * @memberof FileUpload
     */
    async #closeCameraDialog() {
        // Stop camera stream first.
        if (this.#cameraStream) {
            this.#cameraStream.getTracks().forEach((track) => {
                track.stop();
            });

            this.#cameraStream = null;
        }

        if (this.#cameraDialog) {
            // Clean up orientation event listeners.
            if (this.#cameraDialog.orientationHandlers) {
                this.#cameraDialog.orientationHandlers.forEach(({ event, handler }) => {
                    window.removeEventListener(event, handler);
                });
            }

            // Clean up visibility event listeners.
            if (this.#cameraDialog.visibilityHandlers) {
                this.#cameraDialog.visibilityHandlers.forEach(({ event, handler, target }) => {
                    target.removeEventListener(event, handler);
                });
            }

            // Reset zoom level.
            this.#zoomLevel = 1;

            // Close and destroy the dialog-window.
            this.#cameraDialog.close();
            this.#cameraDialog.destroy();
            this.#cameraDialog = null;
            this.#videoElement = null;
        }
    }

    /**
     * Closes the choice dialog.
     *
     * @private
     * @memberof FileUpload
     */
    #closeChoiceDialog() {
        if (this.#choiceDialog) {
            document.body.removeChild(this.#choiceDialog);

            // Unlock scroll on close.
            document.documentElement.style.overflow = '';
            document.body.classList.remove('no-scroll');

            this.#choiceDialog = null;
        }
    }

    /**
     * Tears down the device-camera overlay and releases preview object URLs.
     *
     * @private
     * @memberof FileUpload
     */
    #closeDeviceCameraOverlay() {
        if (!this.#deviceCameraOverlay) {
            return;
        }

        this.#deviceCameraOverlay.querySelectorAll('img').forEach((img) => {
            if (img.src.startsWith('blob:')) {
                URL.revokeObjectURL(img.src);
            }
        });

        this.#deviceCameraOverlay.remove();
        this.#deviceCameraOverlay = null;
    }

    /**
     * Connects to an external preview container based on the preview-container-id attribute.
     * 
     * @private
     * @memberof FileUpload
     */
    #connectToPreviewContainer() {
        // Disconnect from previous container if exists.
        if (this.#previewContainer) {
            this.#previewContainer.removeEventListener('attachment-added', this.#updateFilePresence);
            this.#previewContainer.removeEventListener('attachment-removed', this.#updateFilePresence);
            this.#previewContainer = null;
        }

        // Connect to new container if ID is provided.
        if (this.previewContainerId) {
            this.#previewContainer = document.getElementById(this.previewContainerId);

            if (this.#previewContainer) {
                if (this.readOnly) {
                    this.#previewContainer.readOnly = true;
                }

                // Listen for attachment events to update file presence state.
                this.#previewContainer.addEventListener('attachment-added', () => {
                    this.#updateFilePresence();
                });

                this.#previewContainer.addEventListener('attachment-removed', () => {
                    this.#updateFilePresence();
                });
            }
        }
    }

    /**
     * Creates camera control buttons.
     * 
     * @private
     * @memberof FileUpload
     * @returns {Object} Object containing all created buttons.
     */
    #createCameraButtons() {
        // Create capture button with badge.
        const captureButton = document.createElement('button');
        captureButton.className = 'camera-capture-button';

        const captureButtonIcon = this.cameraIcon;
        const defaultCameraIcon = '📷';

        if (captureButtonIcon) {
            const span = document.createElement('span');

            span.className = `custom-icon ${captureButtonIcon}`;
            captureButton.appendChild(span);
        }
        else {
            const span = document.createElement('span');

            span.className = 'default-icon';
            span.textContent = defaultCameraIcon;
            captureButton.appendChild(span);
        }

        captureButton.title = this.#locales?.takePhoto || 'Foto maken';

        // Create capture badge.
        const captureBadge = document.createElement('span');
        captureBadge.className = 'camera-capture-badge';
        captureBadge.textContent = '0';
        captureBadge.style.display = 'none';
        captureButton.appendChild(captureBadge);

        // Create done button.
        const doneButton = document.createElement('button');
        doneButton.className = 'camera-done-button';
        doneButton.textContent = '✓';
        doneButton.title = this.#locales?.finishAndUpload || 'Voltooien en foto\'s uploaden';

        // Create cancel button.
        const cancelButton = document.createElement('button');
        cancelButton.className = 'camera-cancel-button';
        cancelButton.textContent = '✕';
        cancelButton.title = this.#locales?.cancel || 'Annuleren';

        return {
            cancelButton,
            captureButton,
            doneButton
        };
    }

    /**
     * Creates the camera interface dialog.
     *
     * @private
     * @memberof FileUpload
     */
    #createCameraDialog() {
        // Create headless dialog-window.
        this.#cameraDialog = DialogWindow.create({
            headless: true,
            height: '100%',
            id: 'camera-dialog',
            modal: true,
            width: '100%'
        });

        // Create dialog content.
        const dialogContent = document.createElement('div');
        dialogContent.className = 'camera-dialog-content';

        // Create loading indicator.
        const loadingContainer = document.createElement('div');
        loadingContainer.className = 'camera-loading-container';

        const loadingSpinner = document.createElement('div');
        loadingSpinner.className = 'camera-loading-spinner';

        const loadingText = document.createElement('p');
        loadingText.className = 'camera-loading-text';
        loadingText.textContent = this.#locales?.initializingCamera || 'Camera wordt geïnitialiseerd...';

        loadingContainer.appendChild(loadingSpinner);
        loadingContainer.appendChild(loadingText);

        // Create video container with zoom support.
        const videoContainer = document.createElement('div');
        videoContainer.className = 'camera-video-container';

        // Create video element.
        this.#videoElement = document.createElement('video');
        this.#videoElement.className = 'camera-video';
        this.#videoElement.autoplay = true;
        this.#videoElement.muted = true;
        this.#videoElement.playsinline = true;

        // Prevent picture-in-picture mode and ensure inline playback.
        this.#videoElement.setAttribute('webkit-playsinline', '');
        this.#videoElement.setAttribute('playsInLine', '');
        this.#videoElement.setAttribute('disablePictureInPicture', '');
        this.#videoElement.disablePictureInPicture = true;

        // Hide camera indicator on iOS.
        this.#videoElement.setAttribute('controls', 'false');
        this.#videoElement.setAttribute('controlsList', 'nodownload nofullscreen noremoteplayback');

        // Add video to container.
        videoContainer.appendChild(this.#videoElement);

        // Create controls container.
        const controlsContainer = document.createElement('div');
        controlsContainer.className = 'camera-controls';

        // Create camera buttons.
        const buttons = this.#createCameraButtons();
        const { captureButton, doneButton, cancelButton } = buttons;

        // Assemble dialog.
        controlsContainer.appendChild(cancelButton);
        controlsContainer.appendChild(captureButton);
        controlsContainer.appendChild(doneButton);

        dialogContent.appendChild(loadingContainer);
        dialogContent.appendChild(videoContainer);
        dialogContent.appendChild(controlsContainer);

        // Set the content of the dialog window.
        const dialogElement = this.#cameraDialog.querySelector('dialog');

        if (dialogElement) {
            dialogElement.appendChild(dialogContent);

            // Handle dialog close event (including native close).
            dialogElement.addEventListener('close', () => {
                this.#handleDialogClose();
            });

            // Handle dialog cancel event (ESC key or native close).
            dialogElement.addEventListener('cancel', (event) => {
                event.preventDefault();
                this.#handleDialogClose();
            });
        }

        // Handle orientation change.
        const handleOrientationChange = () => {
            if (this.#isDestroyed) {
                return;
            }

            // Re-apply constraints to handle orientation change.
            if (this.#cameraStream && this.#videoElement) {
                const videoTrack = this.#cameraStream.getVideoTracks()[0];

                if (videoTrack) {
                    videoTrack.applyConstraints({
                        facingMode: this.cameraFacing,
                        height: { ideal: window.innerHeight > window.innerWidth ? FileUpload.VIDEO_HEIGHT : FileUpload.VIDEO_WIDTH },
                        width: { ideal: window.innerHeight > window.innerWidth ? FileUpload.VIDEO_WIDTH : FileUpload.VIDEO_HEIGHT }
                    }).catch(() => {
                        // Ignore constraint errors on orientation change.
                    });
                }
            }
        };

        // Handle visibility change (PWA going to background or native camera button).
        const handleVisibilityChange = () => {
            if (this.#isDestroyed) {
                return;
            }

            if (document.hidden && this.#cameraStream) {
                // App went to background, stop camera.
                this.#handleDialogClose();
            }
        };
        
        // Handle page unload or navigation.
        const handleBeforeUnload = () => {
            if (this.#isDestroyed) {
                return;
            }

            if (this.#cameraStream) {
                this.#handleDialogClose();
            }
        };

        window.addEventListener('orientationchange', handleOrientationChange);
        window.addEventListener('resize', handleOrientationChange);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('pagehide', handleBeforeUnload);

        // Store handlers for cleanup.
        this.#cameraDialog.orientationHandlers = [
            { event: 'orientationchange', handler: handleOrientationChange },
            { event: 'resize', handler: handleOrientationChange }
        ];
        
        this.#cameraDialog.visibilityHandlers = [
            { event: 'visibilitychange', handler: handleVisibilityChange, target: document },
            { event: 'beforeunload', handler: handleBeforeUnload, target: window },
            { event: 'pagehide', handler: handleBeforeUnload, target: window }
        ];

        // Setup event listeners for camera controls and gestures.
        this.#setupCameraEventListeners(buttons, videoContainer);
    }

    /**
     * Creates the choice dialog for upload method selection.
     * Only shown if no stored preference exists for this session.
     *
     * @private
     * @memberof FileUpload
     */
    async #createChoiceDialog() {
        // Check camera availability through permissions manager or fallback.
        let canUseCamera = false;

        if (window.permissionsManager) {
            const cameraStatus = await window.permissionsManager.getCameraPermissionStatus();

            canUseCamera = cameraStatus !== 'unknown';
        }
        else {
            canUseCamera = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
        }

        // If camera is not available, use file input directly.
        if (!canUseCamera) {
            return this.#fileInput.click();
        }

        // Check if user has a stored preference for this session.
        const storedChoice = this.#getStoredChoice();

        if (storedChoice) {
            if (storedChoice === 'camera') {
                return this.#onOpenCamera();
            }
            else if (storedChoice === 'device-camera') {
                return this.#onOpenDeviceCamera();
            }
            else {
                return this.#fileInput.click();
            }
        }

        // Create main overlay.
        this.#choiceDialog = document.createElement('div');
        this.#choiceDialog.className = 'choice-dialog-overlay';

        // Create dialog content.
        const dialogContent = document.createElement('div');
        dialogContent.className = 'choice-dialog-content';
        dialogContent.setAttribute('role', 'dialog');
        dialogContent.setAttribute('aria-modal', 'true');

        // Create unique IDs for ARIA labeling.
        const uid = `choice-${Date.now()}`;
        const dialogTitleId = `${uid}-title`;

        // Create title.
        const title = document.createElement('h3');
        title.textContent = this.#locales?.chooseUploadMethod || 'Kies uploadmethode';
        title.className = 'choice-dialog-title';
        title.id = dialogTitleId;

        dialogContent.setAttribute('aria-labelledby', dialogTitleId);

        // Create button container.
        const buttonContainer = document.createElement('div');
        buttonContainer.className = 'choice-dialog-buttons';

        // Create camera button.
        const cameraButton = document.createElement('button');
        cameraButton.className = 'choice-camera-button';

        const customCameraIcon = this.cameraIcon;
        const defaultCameraIcon = '📷';

        if (customCameraIcon) {
            const span = document.createElement('span');

            span.className = `custom-icon ${customCameraIcon}`;
            cameraButton.appendChild(span);
        }
        else {
            const span = document.createElement('span');

            span.className = 'default-icon';
            span.textContent = defaultCameraIcon;
            cameraButton.appendChild(span);
        }

        const cameraLabel = document.createElement('span');
        cameraLabel.className = 'choice-button-label';
        cameraLabel.textContent = this.#locales?.camera || 'Camera';

        cameraButton.appendChild(cameraLabel);
        cameraButton.addEventListener('click', () => {
            // Store choice if remember checkbox is checked.
            const rememberCheckbox = this.#choiceDialog.querySelector('.choice-remember-checkbox');

            if (rememberCheckbox && rememberCheckbox.checked) {
                this.#setStoredChoice('camera');
            }

            this.#closeChoiceDialog();
            this.#onOpenCamera();
        });

        const deviceCameraButton = document.createElement('button');
        deviceCameraButton.className = 'choice-device-camera-button';

        const customDeviceCameraIcon = this.deviceCameraIcon || this.cameraIcon;
        const defaultDeviceCameraIcon = '📷';

        if (customDeviceCameraIcon) {
            const span = document.createElement('span');

            span.className = `custom-icon ${customDeviceCameraIcon}`;
            deviceCameraButton.appendChild(span);
        }
        else {
            const span = document.createElement('span');

            span.className = 'default-icon';
            span.textContent = defaultDeviceCameraIcon;
            deviceCameraButton.appendChild(span);
        }

        const deviceCameraLabel = document.createElement('span');
        deviceCameraLabel.className = 'choice-button-label';
        deviceCameraLabel.textContent = this.#locales?.deviceCamera || 'Device Camera';

        deviceCameraButton.appendChild(deviceCameraLabel);
        deviceCameraButton.addEventListener('click', () => {
            const rememberCheckbox = this.#choiceDialog.querySelector('.choice-remember-checkbox');

            if (rememberCheckbox && rememberCheckbox.checked) {
                this.#setStoredChoice('device-camera');
            }

            this.#closeChoiceDialog();
            this.#onOpenDeviceCamera();
        });

        // Create files button.
        const fileButton = document.createElement('button');
        fileButton.className = 'choice-files-button';

        const customFilesIcon = this.filesIcon;
        const defaultFilesIcon = '📁';

        if (customFilesIcon) {
            const span = document.createElement('span');

            span.className = `choice-button-icon custom-icon ${customFilesIcon}`;
            fileButton.appendChild(span);
        }
        else {
            const fileIcon = document.createElement('span');
            fileIcon.className = 'choice-button-icon';
            fileIcon.textContent = defaultFilesIcon;
            fileButton.appendChild(fileIcon);
        }

        const fileLabel = document.createElement('span');
        fileLabel.className = 'choice-button-label';
        fileLabel.textContent = this.#locales?.files || 'Bestanden';

        fileButton.appendChild(fileLabel);
        fileButton.addEventListener('click', () => {
            // Store choice if remember checkbox is checked.
            const rememberCheckbox = this.#choiceDialog.querySelector('.choice-remember-checkbox');

            if (rememberCheckbox && rememberCheckbox.checked) {
                this.#setStoredChoice('files');
            }

            this.#closeChoiceDialog();

            // Temporarily remove accept to prevent native camera dialog.
            const originalAccept = this.#fileInput.accept;
            this.#fileInput.removeAttribute('accept');

            this.#fileInput.click();

            // Restore original accept after click.
            requestAnimationFrame(() => {
                if (originalAccept) {
                    this.#fileInput.accept = originalAccept;
                }
            });
        });

        // Create remember choice checkbox.
        const rememberContainer = document.createElement('div');
        rememberContainer.className = 'choice-remember-container';

        const rememberCheckbox = document.createElement('input');
        rememberCheckbox.type = 'checkbox';
        rememberCheckbox.className = 'choice-remember-checkbox';
        rememberCheckbox.id = `${uid}-remember`;

        const rememberLabel = document.createElement('label');
        rememberLabel.className = 'choice-remember-label';
        rememberLabel.htmlFor = rememberCheckbox.id;
        rememberLabel.textContent = this.#locales?.rememberChoice || 'Mijn keuze onthouden';

        rememberContainer.appendChild(rememberCheckbox);
        rememberContainer.appendChild(rememberLabel);

        // Add click outside to close.
        this.#choiceDialog.addEventListener('click', (event) => {
            if (event.target === this.#choiceDialog) {
                this.#closeChoiceDialog();
            }
        });

        // Assemble dialog.
        buttonContainer.appendChild(deviceCameraButton);
        buttonContainer.appendChild(cameraButton);
        buttonContainer.appendChild(fileButton);
        dialogContent.appendChild(title);
        dialogContent.appendChild(buttonContainer);
        dialogContent.appendChild(rememberContainer);

        this.#choiceDialog.appendChild(dialogContent);

        // Lock scroll on open.
        document.documentElement.style.overflow = 'hidden';
        document.body.classList.add('no-scroll');

        document.body.appendChild(this.#choiceDialog);

        // Focus first control and setup keyboard handling.
        requestAnimationFrame(() => {
            this.#choiceDialog.classList.add('active');
            deviceCameraButton.focus();
        });

        // Add ESC key handler.
        this.#choiceDialog.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                this.#closeChoiceDialog();
            }
        });
    }

    /**
     * Finishes photo capture and processes the photos.
     *
     * @private
     * @memberof FileUpload
     */
    async #finishCapture() {
        if (this.#capturedPhotos.length === 0) {
            return;
        }

        await this.#closeCameraDialog();

        const pending = this.#capturedPhotosMetadata.
            flatMap((metadata) => {
                return [metadata.gpsPromise, metadata.downscalePromise];
            }).
            filter(Boolean);

        if (pending.length > 0) {
            await Promise.all(pending);
        }

        // Create FormData with captured photos.
        const formData = new FormData();

        this.#capturedPhotos.forEach((file, index) => {
            formData.append('files', file);
        });

        // Strip the non-serializable promise fields before stringifying metadata.
        if (this.#capturedPhotosMetadata.some((metadata) => {
            return metadata.gps;
        })) {
            const serializable = this.#capturedPhotosMetadata.map(({ gpsPromise, downscalePromise, ...rest }) => {
                return rest;
            });

            formData.append('metadata', JSON.stringify(serializable));
        }

        formData.append('fromCamera', 'true');

        // Upload the photos.
        await this.#performUpload(formData);

        // Clear captured photos.
        this.#capturedPhotos = [];
        this.#capturedPhotosMetadata = [];
    }
    
    /**
     * Lazily creates the hidden `<input type="file" capture>` element used by the
     * device-camera path. Created on first use so desktop sessions don't carry an
     * unused input element.
     *
     * @private
     * @memberof FileUpload
     */
    #ensureDeviceCameraInput() {
        if (this.#deviceCameraInput) {
            return;
        }

        const input = document.createElement('input');

        input.type = 'file';
        input.accept = 'image/*';
        input.capture = this.cameraFacing === 'user' ? 'user' : 'environment';
        input.setAttribute('hidden', '');
        input.style.display = 'none';

        input.addEventListener('change', async () => {
            if (this.#isDestroyed) {
                return;
            }

            const file = input.files && input.files[0];

            // Clearing value is required so picking the same file twice still fires `change`.
            input.value = '';

            if (!file) {
                if (this.#capturedPhotos.length === 0) {
                    this.#closeDeviceCameraOverlay();
                }

                return;
            }

            const metadata = { gps: null, fromCamera: true };
            const photoIndex = this.#capturedPhotos.length;

            this.#capturedPhotos.push(file);
            this.#capturedPhotosMetadata.push(metadata);

            if (!this.#deviceCameraOverlay) {
                this.#openDeviceCameraOverlay();
            }

            this.#addDeviceCameraThumb(file);

            // Resolve GPS off the critical path so the thumb lands instantly.
            metadata.gpsPromise = (async () => {
                try {
                    metadata.gps = await window.gps.getCachedPosition();
                }
                catch {
                    // GPS optional.
                }
            })();

            // Downscale in parallel so the user can keep shooting while re-encode runs.
            metadata.downscalePromise = (async () => {
                try {
                    const downscaled = await this.#downscaleForUpload(file);

                    if (this.#capturedPhotos[photoIndex] === file) {
                        this.#capturedPhotos[photoIndex] = downscaled;
                    }
                }
                catch {
                    // Fall back to the original file on any failure.
                }
            })();
        });

        this.appendChild(input);

        this.#deviceCameraInput = input;
    }

    /**
     * Generates a scoped preference key based on the component context.
     * @returns {string} The preference key for sessionStorage.
     *
     * @private
     * @memberof FileUpload
     */
    #getPreferenceKey() {
        const apiPath = this.apiPath || 'default';
        const containerId = this.previewContainerId || 'default';

        return `fileUploadChoice_${apiPath}_${containerId}`;
    }

    /**
     * Gets the stored choice preference from sessionStorage.
     * @returns {string|null} The stored choice ('camera' or 'files') or null if not set.
     *
     * @private
     * @memberof FileUpload
     */
    #getStoredChoice() {
        try {
            const key = this.#getPreferenceKey();

            return sessionStorage.getItem(key);
        }
        catch {
            return null;
        }
    }

    /**
     * Removes the busy overlay, if present.
     *
     * @private
     * @memberof FileUpload
     */
    #hideUploadBusy() {
        if (!this.#busyOverlay) {
            return;
        }

        this.#busyOverlay.remove();
        this.#busyOverlay = null;
    }

    /**
     * Invokes a callback function by name if it exists in the global scope.
     * @param {string} callbackName The name of the callback function to invoke.
     * @param {*} parameter The parameter to pass to the callback function.
     * 
     * @private
     * @memberof FileUpload
     */
    #invokeCallback(callbackName, parameter) {
        if (callbackName && typeof window[callbackName] === 'function') {
            window[callbackName](parameter);
        }
        else if (callbackName) {
            window.isDebugMode && console.warn(`Callback function '${callbackName}' not found or is not a function.`);
        }
    }

    /**
     * Opens the custom camera interface.
     *
     * @private
     * @memberof FileUpload
     */
    async #openCameraInterface() {
        let loadingContainer = null;

        try {
            // Create dialog structure immediately.
            this.#createCameraDialog();

            // Open dialog immediately to show loading state.
            this.#cameraDialog.open();

            // Get loading container reference.
            loadingContainer = this.#cameraDialog.querySelector('.camera-loading-container');

            // Request stream dimensions matching the current device orientation so the
            // viewfinder isn't letterboxed/pillarboxed from the start.
            const isLandscape = window.innerWidth > window.innerHeight;
            const constraints = {
                video: {
                    facingMode: this.cameraFacing,
                    height: { ideal: isLandscape ? FileUpload.VIDEO_WIDTH : FileUpload.VIDEO_HEIGHT },
                    width: { ideal: isLandscape ? FileUpload.VIDEO_HEIGHT : FileUpload.VIDEO_WIDTH }
                },
                audio: false
            };

            // Add iPhone-specific optimizations.
            if (this.isIos) {
                constraints.video.advanced = [
                    { torch: false },
                    { focusMode: 'continuous' },
                    { exposureMode: 'continuous' },
                    { whiteBalanceMode: 'continuous' }
                ];
                constraints.video.frameRate = { ideal: 30, max: 30 };
                constraints.video.aspectRatio = { ideal: 16 / 9 };
            }

            // Get camera stream directly (permission already verified).
            this.#cameraStream = await navigator.mediaDevices.getUserMedia(constraints);

            // Reset native camera zoom to 1x (prevents iOS starting zoomed in).
            const videoTrack = this.#cameraStream.getVideoTracks()[0];

            if (videoTrack) {
                const capabilities = videoTrack.getCapabilities?.();

                if (capabilities?.zoom) {
                    await videoTrack.applyConstraints({ advanced: [{ zoom: capabilities.zoom.min }] });
                }
            }

            // Configure video element before setting source.
            this.#videoElement.muted = true;
            this.#videoElement.autoplay = true;
            this.#videoElement.playsInline = true;

            // Set source and start immediately.
            this.#videoElement.srcObject = this.#cameraStream;

            // Wait for video to be ready.
            await new Promise((resolve) => {
                if (this.isIos) {
                    // Local playback function.
                    const startPlayback = async () => {
                        try {
                            await this.#videoElement.play();

                            this.#videoElement.controls = false;

                            resolve();
                        }
                        catch (error) {
                            this.#videoElement.controls = false;

                            resolve();
                        }
                    };

                    if (this.#videoElement.readyState >= 2) {
                        startPlayback();
                    }
                    else {
                        this.#videoElement.onloadeddata = startPlayback;
                        this.#videoElement.oncanplay = startPlayback;
                    }
                }
                else {
                    this.#videoElement.onloadedmetadata = () => {
                        this.#videoElement.play().then(resolve).catch(resolve);
                    };
                }
            });

            // Hide loading indicator.
            if (loadingContainer) {
                loadingContainer.classList.add('hidden');
            }

            // Show controls.
            requestAnimationFrame(() => {
                const controls = this.#cameraDialog.querySelector('.camera-controls');

                if (controls) {
                    controls.classList.add('active');
                }
            });
        }
        catch (error) {
            window.isDebugMode && console.error('Error opening camera:', error);

            // Show error in the dialog if it's open.
            if (this.#cameraDialog && loadingContainer) {
                loadingContainer.innerHTML = '';

                const errorIcon = document.createElement('div');
                errorIcon.className = 'camera-error-icon';
                errorIcon.textContent = '⚠';

                const errorText = document.createElement('p');
                errorText.className = 'camera-error-text';
                errorText.textContent = this.#locales?.cameraError || 'Geen toegang tot camera';

                const closeButton = document.createElement('button');
                closeButton.className = 'camera-error-close';
                closeButton.textContent = this.#locales?.close || 'Sluiten';
                closeButton.addEventListener('click', async () => {
                    await this.#closeCameraDialog();

                    // Fallback to file input.
                    this.#fileInput.click();
                });

                loadingContainer.appendChild(errorIcon);
                loadingContainer.appendChild(errorText);
                loadingContainer.appendChild(closeButton);
                loadingContainer.classList.remove('hidden');
            }
            else {
                // Clean up on error.
                await this.#closeCameraDialog();

                // Fallback to file input.
                this.#fileInput.click();
            }
        }
    }

    /**
     * Opens (or refreshes) the overlay shown between device-camera shots. The overlay
     * lets the user take another photo or finish and upload.
     *
     * @private
     * @memberof FileUpload
     */
    #openDeviceCameraOverlay() {
        if (this.#deviceCameraOverlay) {
            return;
        }

        const overlay = document.createElement('div');

        overlay.className = 'choice-dialog-overlay device-camera-overlay active';

        const content = document.createElement('div');

        content.className = 'choice-dialog-content';
        content.setAttribute('role', 'dialog');
        content.setAttribute('aria-modal', 'true');

        const title = document.createElement('h3');

        title.className = 'choice-dialog-title';
        title.textContent = this.#locales?.deviceCamera || 'Device Camera';
        content.appendChild(title);

        const thumbs = document.createElement('div');

        thumbs.className = 'device-camera-thumbs';
        content.appendChild(thumbs);

        const buttons = document.createElement('div');

        buttons.className = 'choice-dialog-buttons';

        const anotherButton = document.createElement('button');

        anotherButton.className = 'choice-camera-button';
        anotherButton.textContent = this.#locales?.takeAnother || 'Take Another';
        anotherButton.addEventListener('click', () => {
            this.#deviceCameraInput.click();
        });

        const doneButton = document.createElement('button');

        doneButton.className = 'choice-files-button';
        doneButton.textContent = this.#locales?.done || 'Done';
        doneButton.addEventListener('click', async () => {
            this.#closeDeviceCameraOverlay();
            await this.#finishCapture();
        });

        const cancelButton = document.createElement('button');

        cancelButton.className = 'choice-cancel-button';
        cancelButton.textContent = this.#locales?.cancel || 'Cancel';
        cancelButton.addEventListener('click', () => {
            this.#capturedPhotos = [];
            this.#capturedPhotosMetadata = [];
            this.#closeDeviceCameraOverlay();
        });

        buttons.appendChild(anotherButton);
        buttons.appendChild(doneButton);
        buttons.appendChild(cancelButton);
        content.appendChild(buttons);
        overlay.appendChild(content);

        document.body.appendChild(overlay);

        this.#deviceCameraOverlay = overlay;
    }

    /**
     * Performs the actual upload to the server.
     * @param {FormData} formData The form data to upload.
     * 
     * @private
     * @memberof FileUpload
     */
    async #performUpload(formData) {
        this.#showUploadBusy();

        try {
            const response = await core.fetch({
                config: {
                    body: formData,
                    method: 'POST'
                },
                url: this.apiPath
            });

            if (response.ok) {
                this.filePresent = true;
                this.dispatchEvent(new CustomEvent('file-uploaded', {
                    bubbles: true,
                    detail: formData.getAll('files')
                }));

                // Read response JSON if content type indicates JSON.
                let responseJson = null;
                const contentType = response.headers.get('content-type');

                if (contentType && contentType.includes('application/json')) {
                    try {
                        responseJson = await response.json();
                    }
                    catch {
                        responseJson = null;
                    }
                }

                // Handle successful upload for integrated preview.
                if (this.#previewContainer && responseJson) {
                    if (responseJson.success && Array.isArray(responseJson.success)) {
                        responseJson.success.forEach((file) => {
                            this.#previewContainer.addAttachment(file);
                        });

                        // Update file presence state after adding attachments.
                        this.#updateFilePresence();
                    }
                }

                // Update validation state when the control is currently in error.
                this.updateValidationState();

                // Invoke success callback with the response and parsed JSON.
                if (this.successCallback) {
                    this.#invokeCallback(this.successCallback, {
                        files: formData.getAll('files'),
                        response: {
                            ...response,
                            json: () => {
                                return Promise.resolve(responseJson);
                            }
                        }
                    });
                }
            }
            else {
                // Handle error response with detailed error processing.
                await this.#handleErrorResponse(response);

                // Invoke error callback with the response.
                if (this.errorCallback) {
                    this.#invokeCallback(this.errorCallback, response);
                }
            }
        }
        catch (error) {
            leanformsNext.onAjaxError(error);

            // Invoke error callback with the error.
            if (this.errorCallback) {
                this.#invokeCallback(this.errorCallback, error);
            }
        }
        finally {
            this.#hideUploadBusy();
        }
    }
    
    /**
     * Sets up event listeners for camera buttons and gestures.
     * @param {Object} buttons The camera buttons.
     * @param {HTMLElement} videoContainer The video container element.
     *
     * @private
     * @memberof FileUpload
     */
    #setupCameraEventListeners(buttons, videoContainer) {
        const { captureButton, doneButton, cancelButton } = buttons;

        // Add event listeners.
        captureButton.addEventListener('click', async () => {
            return await this.#capturePhoto();
        });

        doneButton.addEventListener('click', () => {
            return this.#finishCapture();
        });

        cancelButton.addEventListener('click', () => {
            return this.#cancelCapture();
        });

        // Add touch gesture handlers for pinch-to-zoom.
        let isMultiTouch = false;

        videoContainer.addEventListener('touchstart', (event) => {
            if (event.touches.length === 2) {
                isMultiTouch = true;
                event.preventDefault();
                this.#handlePinchStart(event);
            }
            else {
                isMultiTouch = false;
            }
        }, { passive: false });

        videoContainer.addEventListener('touchmove', (event) => {
            if (isMultiTouch && event.touches.length === 2) {
                event.preventDefault();
                this.#handlePinchGesture(event);
            }
        }, { passive: false });

        videoContainer.addEventListener('touchend', (event) => {
            if (isMultiTouch) {
                this.#handlePinchEnd();
                isMultiTouch = false;
            }
        });

        videoContainer.addEventListener('touchcancel', () => {
            if (isMultiTouch) {
                this.#handlePinchEnd();
                isMultiTouch = false;
            }
        });
    }

    /**
     * Stores the choice preference in sessionStorage.
     * @param {string} choice The choice to store ('camera' or 'files').
     *
     * @private
     * @memberof FileUpload
     */
    #setStoredChoice(choice) {
        try {
            const key = this.#getPreferenceKey();

            sessionStorage.setItem(key, choice);
        }
        catch {
            // Ignore storage errors.
        }
    }

    /**
     * Sets the zoom level within bounds and updates the video.
     * 
     * @private
     * @memberof FileUpload
     * @param {number} zoomLevel The new zoom level.
     */
    #setZoomLevel(zoomLevel) {
        this.#zoomLevel = Math.max(this.#minZoom, Math.min(this.#maxZoom, zoomLevel));
        this.#applyZoom();
    }

    /**
     * Shows the full-screen busy overlay for an in-flight upload.
     *
     * @private
     * @memberof FileUpload
     */
    #showUploadBusy() {
        if (this.#busyOverlay) {
            return;
        }

        const overlay = document.createElement('div');

        overlay.className = 'k-loading-mask file-upload-busy';
        overlay.setAttribute('role', 'status');
        overlay.setAttribute('aria-live', 'polite');

        const image = document.createElement('div');

        image.className = 'k-loading-image';
        overlay.appendChild(image);

        document.body.appendChild(overlay);

        this.#busyOverlay = overlay;
    }

    /**
     * Updates the capture button badge with photo count.
     * 
     * @private
     * @memberof FileUpload
     */
    #updateCaptureBadge() {
        const captureBadge = this.#cameraDialog.querySelector('.camera-capture-badge');
        const photoCount = this.#capturedPhotos.length;

        if (photoCount > 0) {
            captureBadge.textContent = photoCount.toString();
            captureBadge.style.display = 'flex';
        }
        else {
            captureBadge.style.display = 'none';
        }
    }

    /**
     * Updates the done button state.
     * 
     * @private
     * @memberof FileUpload
     */
    #updateDoneButton() {
        const doneButton = this.#cameraDialog.querySelector('.camera-done-button');

        doneButton.disabled = this.#capturedPhotos.length === 0;
    }

    /**
     * Updates file presence state based on current attachments.
     * 
     * @private
     * @memberof FileUpload
     */
    #updateFilePresence() {
        if (this.#previewContainer) {
            const attachments = this.#previewContainer.getAttachments();
            this.filePresent = attachments.length > 0;

            // Update label to reflect current count if counter is enabled.
            if (this.showCounter) {
                this.#updateLabel();
            }
        }
    }

    /**
     * Update label text based on current state.
     * 
     * @private
     * @memberof FileUpload
     */
    #updateLabel() {
        if (this.#buttonText) {
            if (this.filePresent) {
                // When counter is enabled, show count instead of selected label.
                if (this.showCounter && this.#previewContainer) {
                    const attachmentCount = this.#previewContainer.getAttachments().length;
                    this.#buttonText.textContent = `${this.defaultLabel} (${attachmentCount})`;
                }
                else {
                    this.#buttonText.textContent = this.selectedLabel;
                }
            }
            else {
                this.#buttonText.textContent = this.defaultLabel;
            }
        }
    }

    /**
     * Upload file(s) to the server.
     * 
     * @private
     * @memberof FileUpload
     */
    async #uploadFile() {
        if (this.#fileInput.files.length > 0) {
            const formData = new FormData();

            [...this.#fileInput.files].forEach((file) => {
                formData.append('files', file);
            });

            await this.#performUpload(formData);
        }
    }
    // #endregion ********************************************************************************/

}
// #endregion ************************************************************************************/

// #region: Global subscriptions *****************************************************************/
/**
 * Register web component.
 * Defines the custom element 'file-upload' and maps it to the FileUpload class.
 */
customElements.define(FileUpload.tagName, FileUpload);
// #endregion ************************************************************************************/
